import { Box, Button, Dialog, IconButton, MenuItem, Select, Typography, Stack, Tooltip, Divider, Fab, DialogContent, InputAdornment, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useState } from "react";
import { useParams } from "react-router";
import axios from "axios";

// Icons
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Redux & Tools
import { showSnackbar } from "../../../redux/actions";
import { urlAPI } from "../../../config/config";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import { addUpdateTNS } from "../../../tools/forecast/addUpdateTNS";
import { duplicateTNS } from "../../../tools/forecast/duplicateTNS";

// Components
import TNSData from "./TNSData";
import AddUpdateTNS from "../AddUpdate/AddUpdateTNS";
import AlerteDialog from "../../AlerteDialog/AlerteDialog";

const TNS = ({ forecastVersion, forecastDates }: { forecastVersion: number | undefined, forecastDates: string }) => {
    const dispatch = useDispatch();
    const params = useParams();
    const token = useSelector((state: RootState) => state.user.token);

    // --- ÉTATS ---
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [selectedTNS, setSelectedTNS] = useState<string[]>([]);
    const [editId, setEditId] = useState<string>("");

    const monthList = getForecastMonthList(forecastDates);

    // --- ACTIONS ---
    const confirmDelete = async () => {
        try {
            await Promise.all(selectedTNS.map(id => 
                axios.delete(`${urlAPI}/forecast/tns/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ));
            setShowDeleteDialog(false);
            setSelectedTNS([]);
            dispatch(showSnackbar("TNS supprimé(s) avec succès.", "success"));
        } catch (error) {
            dispatch(showSnackbar("Erreur lors de la suppression.", "error"));
        }
    };

    const handleDuplication = () => {
        duplicateTNS(selectedTNS[0], token, setOpenModal, dispatch);
        setSelectedTNS([]);
    };

    return (
        <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
            
            {/* --- BARRE DE FILTRES AVEC TITRE ET STATS (Style Trouver) --- */}
            <Box sx={{ bgcolor: "white", p: 3, borderRadius: 4, mb: 4, border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0,0,0,0.03)" }}>
                
                {/* Ligne du Titre */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a" }}>🛡️ TNS</Typography>
                        {/* TEXTE EN DESSOUS DU TITRE POUR OCCUPER L'ESPACE */}
                        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5, fontWeight: 500 }}>
                            Gestion des rémunérations des dirigeants et cotisations sociales non salariées.
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ my: 2, opacity: 0.6 }} />

                {/* Ligne des Actions */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                    {/* On peut ajouter ici un petit message d'aide ou un filtre si besoin */}
                    <Typography variant="caption" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                        * Les montants sont calculés selon la version de forecast {forecastVersion}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }} />

                    <Button 
                        variant="contained" 
                        startIcon={<AddCircleIcon />} 
                        onClick={() => { setEditId(""); setOpenModal(true); }}
                        sx={{ 
                            borderRadius: 2, 
                            textTransform: "none", 
                            fontWeight: 700, 
                            px: 3,
                            boxShadow: "0px 4px 10px rgba(15, 23, 42, 0.2)"
                        }}
                    >
                        Ajouter un frais
                    </Button>
                </Box>
            </Box>

            {/* --- LISTE DES DONNÉES --- */}
            <Box sx={{ mb: 12 }}>
                <TNSData
                    forecastVersion={forecastVersion}
                    forecastDates={forecastDates}
                    selectedTNS={selectedTNS}
                    setSelectedTNS={setSelectedTNS}
                    openModal={openModal}
                    showDeleteDialog={showDeleteDialog}
                    setEditId={setEditId}
                    setOpenModal={setOpenModal}
                />
            </Box>

            {/* --- DOCK FLOTTANT --- */}
            <Box sx={{ 
                position: "fixed", bottom: 30, right: "50%", transform: "translateX(50%)", 
                display: "flex", alignItems: "center", gap: 2, 
                bgcolor: "white", p: "12px 24px", borderRadius: "100px", border: "1px solid #e2e8f0", 
                boxShadow: "0px 10px 40px rgba(0,0,0,0.1)", zIndex: 1100 
            }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mr: 1 }}>{selectedTNS.length} sélectionnés</Typography>
                
                <Tooltip title="Supprimer">
                    <Fab color="error" size="small" disabled={selectedTNS.length === 0} onClick={() => setShowDeleteDialog(true)}>
                        <DeleteIcon fontSize="small" />
                    </Fab>
                </Tooltip>

                <Tooltip title="Dupliquer">
                    <Fab color="info" size="small" disabled={selectedTNS.length !== 1} onClick={handleDuplication} sx={{color: 'white'}}>
                        <ContentCopyIcon fontSize="small" />
                    </Fab>
                </Tooltip>

                <Divider orientation="vertical" flexItem />

                <Fab color="primary" size="medium" onClick={() => { setEditId(""); setOpenModal(true); }}>
                    <AddCircleIcon />
                </Fab>
            </Box>

            {/* MODALE */}
            <Dialog open={openModal} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: 4 } }}>
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" sx={{fontWeight: 800}}>{editId ? "Modifier" : "Ajouter"} un TNS</Typography>
                    <IconButton onClick={() => setShowConfirmDialog(true)}><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    <AddUpdateTNS
                        tnsId={editId}
                        handleSubmit={addUpdateTNS}
                        forecastId={params.id}
                        version={forecastVersion}
                        setOpenModal={setOpenModal}
                        monthList={monthList}
                        setShowConfirmDialog={setShowConfirmDialog}
                    />
                </DialogContent>
            </Dialog>

            {/* ALERTES */}
            <AlerteDialog open={showConfirmDialog} handleClose={() => setShowConfirmDialog(false)} deleteProject={() => { setShowConfirmDialog(false); setOpenModal(false); setEditId(""); }} title="Abandonner ?" contentText="Modifications non enregistrées." color="error" />
            <AlerteDialog open={showDeleteDialog} handleClose={() => setShowDeleteDialog(false)} deleteProject={confirmDelete} title="Supprimer ?" contentText="Cette action est irréversible." color="error" />
        </Box>
    );
};

export default TNS;